import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories, mainSiteUrl, seoArticles } from '@/mocks/vigorbuy-data';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  function handleHashNav(hash: string) {
    if (window.location.pathname !== '/') {
      navigate('/' + hash);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-xl font-bold text-foreground-900 whitespace-nowrap">
            VigorBuy<span className="text-primary-500">Spreadsheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <button onClick={() => handleHashNav('#categories')} className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer bg-transparent">Categories</button>
            <button onClick={() => handleHashNav('#comparison')} className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer bg-transparent">Comparison</button>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideDropdownOpen(!guideDropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                <i className={`text-xs transition-transform ${guideDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 z-30">
                  {seoArticles.map((article, idx) => (
                    <Link
                      key={idx}
                      to={`/${article.slug}`}
                      onClick={() => setGuideDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      <span className="line-clamp-1">{article.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => handleHashNav('#faq')} className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer bg-transparent">FAQ</button>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div ref={mobileRef} className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto fixed top-16 left-0 right-0 z-50 shadow-xl">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 text-sm font-semibold text-foreground-900 py-2.5 px-2 cursor-pointer">
              <i className="ri-home-line text-primary-500"></i> Home
            </Link>

            <div className="border-t border-background-200/60 my-2"></div>

            <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-2 py-1">Categories</div>
            {categories.map((cat, idx) => (
              <a
                key={idx}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 text-sm text-foreground-700 py-2 px-2 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-primary-500">{idx + 1}</span>
                </span>
                {cat.name}
              </a>
            ))}

            <div className="border-t border-background-200/60 my-2"></div>

            <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-2 py-1">Shopping Guides</div>
            {seoArticles.map((article, idx) => (
              <Link
                key={idx}
                to={`/${article.slug}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 text-sm text-foreground-700 py-2 px-2 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-article-line text-secondary-500 text-xs"></i>
                </span>
                <span className="line-clamp-1">{article.title}</span>
              </Link>
            ))}

            <div className="border-t border-background-200/60 my-2"></div>

            <button onClick={() => { setMobileOpen(false); handleHashNav('#comparison'); }} className="flex items-center gap-3 text-sm text-foreground-700 py-2 px-2 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer text-left bg-transparent">
              <i className="ri-scales-line text-foreground-400"></i> Comparison
            </button>
            <button onClick={() => { setMobileOpen(false); handleHashNav('#faq'); }} className="flex items-center gap-3 text-sm text-foreground-700 py-2 px-2 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer text-left bg-transparent">
              <i className="ri-question-line text-foreground-400"></i> FAQ
            </button>

            <div className="mt-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                VigorBuy Spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}